import { useState } from "react";
import { http, requestErrorMessage } from "@/lib/erp";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const describe = (counts) => Object.entries(counts || {}).map(([name, count]) => `${count} ${name.replaceAll("_", " ")}`).join(", ") || "None";

export default function BusinessDataCleanup() {
  const [plan, setPlan] = useState(null);
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  async function review() {
    setBusy(true); setError(""); setResult(null); setPlan(null); setConfirmation("");
    try { setPlan((await http.get("/admin/business-data/review")).data); }
    catch (e) { setError(requestErrorMessage(e, "review business data")); }
    finally { setBusy(false); }
  }
  async function apply() {
    setBusy(true); setError("");
    try {
      const response = await http.post("/admin/business-data/apply", { plan_hash: plan.plan_hash, confirmation });
      setResult(response.data); setPlan(null); setConfirmation("");
      window.dispatchEvent(new Event("vg-data-imported"));
    } catch (e) {
      setError(requestErrorMessage(e, "apply cleanup")); setPlan(null); setConfirmation("");
    } finally { setBusy(false); }
  }
  async function downloadBackup() {
    setBusy(true); setError("");
    try {
      const data = (await http.get(`/admin/business-data/backup/${result.backup_id}`)).data;
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
      const link = document.createElement("a"); link.href = url; link.download = "business-data-before-cleanup.json"; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setError(requestErrorMessage(e, "download backup")); }
    finally { setBusy(false); }
  }
  return <Card className="p-5 mb-6 space-y-3" data-testid="business-data-cleanup">
    <h2 className="font-semibold">Remove dummy data & keep uploaded records</h2>
    <p className="text-sm text-muted-foreground">Owner-only action for this company. Copies the approved uploaded medicines, invoices, quotations and receipts. Existing real records, settings and the Walk-in Customer are preserved. Historical invoices do not change stock. A private backup is saved before changes.</p>
    <Button onClick={review} disabled={busy} variant="outline" data-testid="business-data-review">{busy ? "Please wait…" : "Review cleanup & uploaded data"}</Button>
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    {plan && <div className="text-sm space-y-2" data-testid="business-data-plan">
      <p><strong>Dummy records to remove:</strong> {describe(plan.remove)}</p>
      <p><strong>Uploaded records to add:</strong> {describe(plan.insert)}</p>
      <p><strong>Verified opening balances / numbering to reconcile:</strong> {describe(plan.update)}</p>
      {!plan.change_count ? <p className="text-green-700">Already clean: your uploaded records are present. No changes needed.</p> : <>
        <p>Review the counts above. Type <strong>REMOVE DUMMY DATA</strong> to confirm removal and import.</p>
        <Input aria-label="Cleanup confirmation" value={confirmation} onChange={e => setConfirmation(e.target.value)} data-testid="business-data-confirmation" />
        <Button onClick={apply} disabled={busy || confirmation !== "REMOVE DUMMY DATA"} data-testid="business-data-apply">Apply cleanup & keep uploads</Button>
      </>}
    </div>}
    {result && <div role="status" className="text-sm space-y-2">
      <p className="text-green-700">{result.already_clean ? "Already clean; no data changed." : "Cleanup and import completed. Reload any other open ERP pages to see the updated records."}</p>
      {result.backup_id && <Button variant="outline" disabled={busy} onClick={downloadBackup}>Download pre-cleanup backup</Button>}
    </div>}
  </Card>;
}